import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import MediaManager from './MediaManager.jsx';
import MapPanel from '../../components/MapPanel.jsx';
import { ErrorState, Field, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatXaf } from '../../lib/format.js';
import { AVAILABILITY_LABEL, LETTING, isMultiUnit } from '../../lib/letting.js';

// A unit's own status, in the words the marketplace uses.
const UNIT_AVAILABILITY = { vacant: 'available', reserved: 'pending', occupied: 'occupied', maintenance: 'unavailable' };

const BLANK = {
  title: '',
  description: '',
  city: '',
  neighbourhood: '',
  address: '',
  price: '',
  depositMonths: 2,
  propertyType: 'apartment',
  rooms: 1,
  bathrooms: 1,
  areaSqm: '',
  furnished: false,
  amenities: [],
  status: 'available',
  letting: 'whole',
  listingKind: 'rent',
  negotiable: false,
  plotSqm: '',
  landTitle: '',
  landUse: '',
  parcelId: '',
  latitude: '',
  longitude: ''
};

const PropertyEditor = () => {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const { cities, propertyTypes, amenities, landTitles, landUses, city: lookupCity } = useMeta();

  // "Sell it" from My land arrives with the plot already chosen.
  const [searchParams] = useSearchParams();
  const sellPlot = searchParams.get('sell');
  const [form, setForm] = useState(
    sellPlot ? { ...BLANK, listingKind: 'sale', propertyType: 'land', parcelId: sellPlot } : BLANK
  );
  const [saleBlockedBy, setSaleBlockedBy] = useState(null);
  // The plots on the land register this landlord could sell.
  const myLand = useFetch('/land/mine');
  const sellable = (myLand.data?.items || []).filter((plot) => plot.verificationStatus !== 'rejected');
  const [media, setMedia] = useState([]);
  const [units, setUnits] = useState([]);
  const [sitePlanUrl, setSitePlanUrl] = useState(null);
  const [uploadingPlan, setUploadingPlan] = useState(false);
  const [moderation, setModeration] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(editing);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  useTitle(editing ? 'Edit listing' : 'Add a listing');

  useEffect(() => {
    if (!editing) return;
    setLoading(true);
    api
      .get(`/properties/${id}`)
      .then(({ property }) => {
        setForm({
          title: property.title,
          description: property.description || '',
          city: property.city,
          neighbourhood: property.neighbourhood || '',
          address: property.address || '',
          price: String(property.price),
          depositMonths: property.depositMonths,
          propertyType: property.propertyType,
          rooms: property.rooms,
          bathrooms: property.bathrooms,
          areaSqm: property.areaSqm ? String(property.areaSqm) : '',
          furnished: property.furnished,
          amenities: property.amenities || [],
          status: property.status,
          letting: property.letting || 'whole',
          listingKind: property.listingKind || 'rent',
          negotiable: Boolean(property.negotiable),
          plotSqm: property.plotSqm ? String(property.plotSqm) : '',
          landTitle: property.landTitle || '',
          landUse: property.landUse || '',
          parcelId: property.parcelId ? String(property.parcelId) : '',
          latitude: property.latitude ?? '',
          longitude: property.longitude ?? ''
        });
        setMedia(property.media || []);
        setUnits(property.units || []);
        setSitePlanUrl(property.sitePlanUrl || null);
        setSaleBlockedBy(property.saleBlockedBy || null);
        setModeration({ status: property.moderationStatus, note: property.moderationNote });
      })
      .catch((error) => setLoadError(error.message))
      .finally(() => setLoading(false));
  }, [editing, id]);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const toggleAmenity = (slug) =>
    set({
      amenities: form.amenities.includes(slug)
        ? form.amenities.filter((item) => item !== slug)
        : [...form.amenities, slug]
    });

  const uploadSitePlan = async (file) => {
    if (!file) return;
    setUploadingPlan(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await api.upload(`/properties/${id}/site-plan`, formData);
      setSitePlanUrl(result.sitePlanUrl);
      toast.success('Site plan saved. Buyers can open it from the listing.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPlan(false);
    }
  };

  const removeSitePlan = async () => {
    try {
      await api.del(`/properties/${id}/site-plan`);
      setSitePlanUrl(null);
      toast.success('Site plan removed.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      city: form.city,
      neighbourhood: form.neighbourhood.trim() || null,
      address: form.address.trim() || null,
      letting: form.listingKind === 'sale' ? 'whole' : form.letting,
      listingKind: form.listingKind,
      negotiable: Boolean(form.negotiable),
      plotSqm: form.plotSqm ? Number(form.plotSqm) : null,
      landTitle: form.landTitle || null,
      landUse: form.landUse || null,
      parcelId: form.listingKind === 'sale' && form.parcelId ? Number(form.parcelId) : null,
      // A building's rent is each apartment's own; the server keeps the headline
      // figure at the cheapest of them.
      price: isMultiUnit(form) ? Number(form.price) || 0 : Number(form.price),
      depositMonths: Number(form.depositMonths),
      propertyType: form.propertyType,
      rooms: Number(form.rooms),
      bathrooms: Number(form.bathrooms),
      areaSqm: form.areaSqm ? Number(form.areaSqm) : null,
      furnished: Boolean(form.furnished),
      amenities: form.amenities,
      status: form.status,
      latitude: form.latitude === '' ? null : Number(form.latitude),
      longitude: form.longitude === '' ? null : Number(form.longitude)
    };

    try {
      if (editing) {
        const result = await api.patch(`/properties/${id}`, payload);
        toast.success(
          result.sentForReview
            ? 'Saved. Because you changed the price or description it goes back to an administrator for review.'
            : 'Listing saved.'
        );
        setModeration({ status: result.property.moderationStatus, note: result.property.moderationNote });
      } else {
        const result = await api.post('/properties', payload);
        toast.success(
          result.pendingApproval
            ? 'Listing created. An administrator will review it, usually the same day. Add your photos now.'
            : 'Listing created and published.'
        );
        if (isMultiUnit(result.property)) {
          toast.success(`Now add each of the ${LETTING[form.letting].units}, with its own rent.`);
          navigate(`/landlord/properties/${result.property.id}/units`, { replace: true });
        } else {
          navigate(`/landlord/properties/${result.property.id}/edit`, { replace: true });
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.details) {
        setErrors(error.details);
        toast.error('Some fields need attention.');
      } else {
        toast.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (loadError) {
    return (
      <div className="container section">
        <ErrorState message={loadError} />
      </div>
    );
  }

  const town = lookupCity(form.city);
  const mapPoint =
    form.latitude !== '' && form.longitude !== ''
      ? { id: 'draft', latitude: Number(form.latitude), longitude: Number(form.longitude) }
      : town
        ? { id: 'town', latitude: town.lat, longitude: town.lng }
        : null;

  const price = Number(form.price) || 0;
  const sale = form.listingKind === 'sale';
  const land = form.propertyType === 'land';
  const multi = !sale && isMultiUnit(form);
  const words = LETTING[form.letting] || LETTING.whole;

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{editing ? 'Edit listing' : 'Add a listing'}</h1>
          <p className="muted">
            {editing
              ? 'Changes to the price, title or description send the listing back for a quick review.'
              : 'Save the details first, then add your pictures and video.'}
          </p>
        </div>
        <Link to="/landlord/properties" className="btn btn-ghost">
          Back to my listings
        </Link>
      </header>

      {moderation && moderation.status !== 'approved' ? (
        <div className={`notice notice-${moderation.status === 'rejected' ? 'bad' : 'warn'}`}>
          <StatusBadge status={moderation.status} />{' '}
          {moderation.status === 'rejected'
            ? `An administrator rejected this listing. ${moderation.note || ''}`
            : 'This listing is waiting for approval and is not visible to tenants yet.'}
        </div>
      ) : null}

      <form className="editor-layout" onSubmit={submit}>
        <div className="editor-main">
          <section className="panel">
            <h2>Rent it out or sell it?</h2>
            <div className="letting-choice is-two" role="radiogroup" aria-label="Rent or sell">
              {[
                ['rent', 'Rent it out', 'Tenants apply, and you draw up a lease and collect rent here.'],
                ['sale', 'Sell it', 'A house or a piece of land. Buyers make offers; you accept one and meet to complete the sale.']
              ].map(([key, label, hint]) => (
                <label key={key} className={`letting-option ${form.listingKind === key ? 'is-chosen' : ''}`}>
                  <input
                    type="radio"
                    name="listingKind"
                    value={key}
                    checked={form.listingKind === key}
                    onChange={() => set({ listingKind: key, status: ['pending', 'rented'].includes(form.status) ? 'available' : form.status })}
                  />
                  <strong>{label}</strong>
                  <span>{hint}</span>
                </label>
              ))}
            </div>
          </section>

          {sale ? null : (
            <section className="panel">
              <h2>What are you letting?</h2>
              <div className="letting-choice" role="radiogroup" aria-label="How it is let">
                {Object.entries(LETTING).map(([key, option]) => (
                  <label key={key} className={`letting-option ${form.letting === key ? 'is-chosen' : ''}`}>
                    <input
                      type="radio"
                      name="letting"
                      value={key}
                      checked={form.letting === key}
                      onChange={() =>
                        set({
                          letting: key,
                          // A building is published or a draft; whether it can be had
                          // is down to its apartments.
                          status: key !== 'whole' && ['pending', 'rented'].includes(form.status) ? 'available' : form.status
                        })
                      }
                    />
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </label>
                ))}
              </div>
            </section>
          )}

          <section className="panel">
            <h2>The basics</h2>

            <Field
              label="Title"
              hint="Say what it is and roughly where. For example: Two bedroom flat in a quiet Tiko compound."
              error={errors.title}
              required
            >
              <input type="text" value={form.title} onChange={(event) => set({ title: event.target.value })} required />
            </Field>

            <Field
              label="Description"
              hint="Be specific about water, light, the toilet, the road and the compound. It saves you answering the same questions twenty times."
              error={errors.description}
            >
              <textarea rows="7" value={form.description} onChange={(event) => set({ description: event.target.value })} />
            </Field>

            <div className="grid-3">
              <Field label="Town" error={errors.city} required>
                <select value={form.city} onChange={(event) => set({ city: event.target.value })} required>
                  <option value="">Choose a town</option>
                  {cities.map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name} ({item.region})
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Neighbourhood" error={errors.neighbourhood}>
                <input
                  type="text"
                  value={form.neighbourhood}
                  onChange={(event) => set({ neighbourhood: event.target.value })}
                  placeholder="Molyko, Bonapriso, Tiko Town"
                />
              </Field>

              <Field label="Type" error={errors.propertyType} required>
                <select value={form.propertyType} onChange={(event) => set({ propertyType: event.target.value })}>
                  {propertyTypes.map((type) => (
                    <option key={type.slug} value={type.slug}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field
              label="Street or landmark"
              hint="Not shown in full to tenants until you agree a viewing."
              error={errors.address}
            >
              <input type="text" value={form.address} onChange={(event) => set({ address: event.target.value })} />
            </Field>
          </section>

          <section className="panel">
            <h2>{sale ? 'Price and size' : multi ? 'The building' : 'Size and rent'}</h2>

            {multi ? (
              <p className="field-hint">
                Each of the {words.units} gets its own rent, size and status. You add them{' '}
                {editing ? 'below' : 'straight after saving this'}. The listing then shows the cheapest one that is free.
              </p>
            ) : null}

            <div className="grid-3">
              {multi ? null : (
                <Field
                  label={sale ? 'Asking price in FCFA' : 'Monthly rent in FCFA'}
                  hint={sale && price > 0 ? formatXaf(price) : undefined}
                  error={errors.price}
                  required
                >
                  <input
                    type="number"
                    min="1000"
                    step="1"
                    value={form.price}
                    onChange={(event) => set({ price: event.target.value })}
                    required
                  />
                </Field>
              )}

              {sale ? (
                <Field label="Price">
                  <select
                    value={form.negotiable ? 'true' : 'false'}
                    onChange={(event) => set({ negotiable: event.target.value === 'true' })}
                  >
                    <option value="false">Fixed</option>
                    <option value="true">Negotiable</option>
                  </select>
                </Field>
              ) : null}

              {sale ? (
                <Field label="Plot size in m2" hint={Number(form.plotSqm) >= 10000 ? `${(Number(form.plotSqm) / 10000).toFixed(2)} hectares` : 'One hectare is 10 000 m2.'} error={errors.plotSqm}>
                  <input type="number" min="0" value={form.plotSqm} onChange={(event) => set({ plotSqm: event.target.value })} />
                </Field>
              ) : null}

              {sale ? null : (
                <Field label="Deposit in months of rent" error={errors.depositMonths}>
                  <input
                    type="number"
                    min="0"
                    max="12"
                    value={form.depositMonths}
                    onChange={(event) => set({ depositMonths: event.target.value })}
                  />
                </Field>
              )}

              {land ? null : (
                <>
                  <Field label="Floor area in m2" error={errors.areaSqm}>
                    <input
                      type="number"
                      min="0"
                      value={form.areaSqm}
                      onChange={(event) => set({ areaSqm: event.target.value })}
                    />
                  </Field>

                  <Field label={multi ? 'Rooms in the whole building' : 'Rooms'} error={errors.rooms}>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={form.rooms}
                      onChange={(event) => set({ rooms: event.target.value })}
                    />
                  </Field>

                  <Field label="Bathrooms" error={errors.bathrooms}>
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={form.bathrooms}
                      onChange={(event) => set({ bathrooms: event.target.value })}
                    />
                  </Field>

                  <Field label="Furnished">
                    <select
                      value={form.furnished ? 'true' : 'false'}
                      onChange={(event) => set({ furnished: event.target.value === 'true' })}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </Field>
                </>
              )}
            </div>

            {price > 0 && !multi && !sale ? (
              <p className="field-hint">
                A tenant will need {formatXaf(price * 12 + price * Number(form.depositMonths))} to move in: 12 months rent plus{' '}
                {form.depositMonths} month{Number(form.depositMonths) === 1 ? '' : 's'} deposit.
              </p>
            ) : null}
          </section>

          {sale ? (
            <section className="panel">
              <h2>The land and its papers</h2>
              <p className="field-hint">
                What a buyer asks first. Be exact: a buyer who finds the papers are not what the listing said walks away,
                and reports it.
              </p>

              <Field
                label="The plot on the land register"
                hint="Every sale is of a registered plot, so it cannot be sold twice. Buyers can see its history."
                error={errors.parcelId}
                required
              >
                <select
                  value={form.parcelId}
                  onChange={(event) => {
                    const plot = sellable.find((item) => String(item.id) === event.target.value);
                    set({ parcelId: event.target.value, plotSqm: plot && !form.plotSqm ? String(plot.areaSqm) : form.plotSqm });
                  }}
                >
                  <option value="">Choose one of your registered plots</option>
                  {sellable.map((plot) => (
                    <option key={plot.id} value={plot.id} disabled={Boolean(plot.forSale && String(plot.forSale.propertyId) !== id)}>
                      {plot.reference} - {plot.village}
                      {plot.plotNumber ? `, plot ${plot.plotNumber}` : ''}
                      {plot.verified ? '' : ' (still being checked)'}
                      {plot.forSale && String(plot.forSale.propertyId) !== id ? ' (already on sale)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
              {!form.parcelId ? (
                <div className="notice notice-warn">
                  Not registered yet? <Link to="/my/land/new">Register the plot</Link> from the site plan its chief signed. The
                  listing stays a draft until it has a verified plot.
                </div>
              ) : saleBlockedBy === 'unverified' || sellable.find((plot) => String(plot.id) === form.parcelId)?.verified === false ? (
                <div className="notice notice-info">
                  The plot is still being checked on the land register. The listing goes public as soon as it is verified.
                </div>
              ) : saleBlockedBy === 'conflict' ? (
                <div className="notice notice-bad">A conflict has been reported over this land. The listing is hidden until it is settled.</div>
              ) : null}
              <div className="grid-2">
                <Field label="Land title" error={errors.landTitle}>
                  <select value={form.landTitle} onChange={(event) => set({ landTitle: event.target.value })}>
                    <option value="">Choose one</option>
                    {landTitles.map((title) => (
                      <option key={title.slug} value={title.slug}>
                        {title.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="What the land is for" error={errors.landUse}>
                  <select value={form.landUse} onChange={(event) => set({ landUse: event.target.value })}>
                    <option value="">Choose one</option>
                    {landUses.map((use) => (
                      <option key={use.slug} value={use.slug}>
                        {use.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="site-plan-box">
                <div>
                  <strong>Site plan</strong>
                  <p className="field-hint">
                    The surveyor's drawing with the plot number and boundary beacons, as a photo or a PDF. Buyers see it on
                    the listing.
                  </p>
                  {sitePlanUrl ? (
                    <a href={sitePlanUrl} target="_blank" rel="noreferrer">
                      Open the site plan on file
                    </a>
                  ) : null}
                </div>
                {editing ? (
                  <div className="row-actions">
                    <label className={`btn btn-outline btn-sm ${uploadingPlan ? 'is-busy' : ''}`}>
                      {uploadingPlan ? 'Uploading...' : sitePlanUrl ? 'Replace it' : 'Upload the site plan'}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        hidden
                        disabled={uploadingPlan}
                        onChange={(event) => uploadSitePlan(event.target.files?.[0])}
                      />
                    </label>
                    {sitePlanUrl ? (
                      <button type="button" className="link-btn danger-link" onClick={removeSitePlan}>
                        Remove
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <p className="field-hint">You can upload it as soon as the listing is created.</p>
                )}
              </div>
            </section>
          ) : null}

          <section className="panel">
            <h2>{land ? 'What the land has' : 'What the house has'}</h2>
            <p className="field-hint">Tick everything that applies. Tenants filter on these, so an unticked box costs you views.</p>
            <div className="amenity-grid">
              {amenities.map((amenity) => (
                <label key={amenity.slug} className="check">
                  <input
                    type="checkbox"
                    checked={form.amenities.includes(amenity.slug)}
                    onChange={() => toggleAmenity(amenity.slug)}
                  />
                  <span>{amenity.label}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="panel">
            <h2>Position on the map</h2>
            <p className="field-hint">
              Leave these blank and the pin sits on the middle of town. Fill them in for a more useful pin. You can read
              the numbers off any phone map app.
            </p>
            <div className="grid-2">
              <Field label="Latitude" error={errors.latitude}>
                <input
                  type="number"
                  step="0.000001"
                  value={form.latitude}
                  onChange={(event) => set({ latitude: event.target.value })}
                  placeholder="4.0752"
                />
              </Field>
              <Field label="Longitude" error={errors.longitude}>
                <input
                  type="number"
                  step="0.000001"
                  value={form.longitude}
                  onChange={(event) => set({ longitude: event.target.value })}
                  placeholder="9.3603"
                />
              </Field>
            </div>
            {mapPoint ? <MapPanel points={[mapPoint]} single height={260} /> : null}
          </section>

          {editing && multi ? (
            <section className="panel">
              <div className="section-head">
                <h2>The {words.units}</h2>
                <Link to={`/landlord/properties/${id}/units`} className="btn btn-outline btn-sm">
                  {units.length ? `Manage the ${words.units}` : `Add the ${words.units}`}
                </Link>
              </div>
              {units.length === 0 ? (
                <p className="field-hint">
                  None yet. Until you add them nobody can apply, because there is nothing to choose from.
                </p>
              ) : (
                <ul className="unit-chips">
                  {units.map((unit) => {
                    const where = UNIT_AVAILABILITY[unit.status] || 'unavailable';
                    return (
                      <li key={unit.id}>
                        <strong>{unit.label}</strong>
                        <span>{formatXaf(unit.rent)}</span>
                        <StatusBadge status={where} label={AVAILABILITY_LABEL[where]} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : null}

          {editing ? (
            <section className="panel">
              <h2>Pictures and video</h2>
              <MediaManager propertyId={id} items={media} onChange={setMedia} />
            </section>
          ) : null}
        </div>

        <aside className="editor-side">
          <div className="panel sticky-panel">
            <h2>Publish</h2>

            {sale ? (
              <Field label="Where the sale stands" hint="Mark it sold from the accepted offer, on your Offers page - that hands the land to the buyer.">
                <select value={form.status} onChange={(event) => set({ status: event.target.value })}>
                  <option value="available">For sale</option>
                  <option value="pending">Under offer</option>
                  {form.status === 'rented' ? <option value="rented">Sold</option> : null}
                  <option value="draft">Draft, hidden</option>
                </select>
              </Field>
            ) : multi ? (
              <Field
                label="On the marketplace"
                hint={`Whether each of the ${words.units} is available, pending or rented comes from the ${words.units} themselves.`}
              >
                <select value={form.status} onChange={(event) => set({ status: event.target.value })}>
                  <option value="available">Published</option>
                  <option value="draft">Draft, hidden</option>
                </select>
              </Field>
            ) : (
              <Field label="Availability" hint="Draft keeps it hidden while you finish it off.">
                <select value={form.status} onChange={(event) => set({ status: event.target.value })}>
                  <option value="available">Available now</option>
                  <option value="pending">Pending, somebody is in the process</option>
                  <option value="rented">Rented out</option>
                  <option value="draft">Draft, hidden</option>
                </select>
              </Field>
            )}

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Save changes' : 'Create listing'}
            </button>

            {editing ? (
              <Link to={`/properties/${id}`} className="btn btn-outline btn-block">
                {sale ? 'View as buyers see it' : 'View as a tenant sees it'}
              </Link>
            ) : (
              <p className="field-hint">
                You will be able to upload pictures as soon as the listing is created.
              </p>
            )}

            <ul className="checklist">
              <li className={form.title.length > 8 ? 'is-done' : ''}>A clear title</li>
              <li className={form.description.length > 120 ? 'is-done' : ''}>A description worth reading</li>
              <li className={form.city ? 'is-done' : ''}>Town chosen</li>
              {sale ? (
                <>
                  <li className={sellable.find((plot) => String(plot.id) === form.parcelId)?.verified ? 'is-done' : ''}>
                    A verified plot on the land register
                  </li>
                  <li className={price > 0 ? 'is-done' : ''}>Asking price set</li>
                  <li className={form.landTitle ? 'is-done' : ''}>Land title stated</li>
                  <li className={sitePlanUrl ? 'is-done' : ''}>Site plan uploaded</li>
                </>
              ) : multi ? (
                <li className={units.length > 0 ? 'is-done' : ''}>The {words.units} added, each with its rent</li>
              ) : (
                <li className={price > 0 ? 'is-done' : ''}>Rent set</li>
              )}
              <li className={form.amenities.length >= 3 ? 'is-done' : ''}>At least three amenities ticked</li>
              <li className={media.filter((item) => item.type === 'image').length >= 3 ? 'is-done' : ''}>
                Three or more pictures
              </li>
              <li className={media.some((item) => item.type === 'video') ? 'is-done' : ''}>A walk-through video</li>
            </ul>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default PropertyEditor;
